import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import RoleBasedNavigation from '@/components/RoleBasedNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select';
import { 
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Check, ChevronsUpDown, ArrowUpDown, ArrowUp, ArrowDown, CalendarIcon } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { cn } from '@/lib/utils';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import api from '@/lib/api';
import { 
  Attendance, 
  AttendanceFilters, 
  AttendanceStatistics 
} from '@/types/attendance';
import { 
  Download, 
  Search, 
  X, 
  Calendar, 
  Clock, 
  User, 
  Phone,
  TrendingUp,
  TrendingDown,
  BarChart3,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
  FileSpreadsheet,
  Activity,
  FileText,
  Bot,
  FolderOpen
} from 'lucide-react';
import ReportsCenter from '@/components/reports/ReportsCenter';
import { useAuth } from '@/contexts/AuthContext';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from 'recharts';

// Helper para renderizar atendente com ícone de robô se for IA
const renderAttendant = (attendant: string | undefined | null) => {
  if (attendant === 'IA') {
    return (
      <div className="flex items-center gap-2">
        <Bot className="h-4 w-4 text-primary-600" />
        <span>IA</span>
      </div>
    );
  }
  return attendant || '-';
};

const AttendancesContent: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  // Estados
  const [attendances, setAttendances] = useState<Attendance[]>([]);
  const [statistics, setStatistics] = useState<AttendanceStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState<'excel' | 'pdf' | null>(null);
  const [selectedAttendance, setSelectedAttendance] = useState<Attendance | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showReportsCenter, setShowReportsCenter] = useState(false);
  
  // Estados para período de data
  type PeriodType = 'current_month' | 'last_month' | 'custom';
  const [periodType, setPeriodType] = useState<PeriodType>('current_month');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');
  
  // Estados para dropdowns multiselect
  const [attendantOptions, setAttendantOptions] = useState<string[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
  const [connectionOptions, setConnectionOptions] = useState<string[]>([]);
  const [selectedAttendants, setSelectedAttendants] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedConnections, setSelectedConnections] = useState<string[]>([]);

  // Paginação
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  // Função para calcular datas do período
  const getPeriodDates = useCallback((period: PeriodType, customFrom?: string, customTo?: string): { from: string; to: string } => {
    const now = new Date();
    const today = format(now, 'yyyy-MM-dd');
    
    switch (period) {
      case 'current_month':
        const firstDayCurrent = format(startOfMonth(now), 'yyyy-MM-dd');
        return { from: firstDayCurrent, to: today };
      
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        const firstDayLast = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
        const lastDayLast = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
        return { from: firstDayLast, to: lastDayLast };
      
      case 'custom':
        return {
          from: customFrom || format(startOfMonth(now), 'yyyy-MM-dd'),
          to: customTo || today,
        };
      
      default:
        return { from: format(startOfMonth(now), 'yyyy-MM-dd'), to: today };
    }
  }, []);

  // Inicializar filtros com mês atual
  const initializeFilters = useCallback((period: PeriodType): AttendanceFilters => {
    const dates = getPeriodDates(period);
    return {
      page: 1,
      limit: 50,
      sort_by: 'created_at',
      sort_order: 'DESC',
        start_date: dates.from,
        end_date: dates.to,
    };
  }, [getPeriodDates]);


  // Filtros - inicializar com mês atual
  const [filters, setFilters] = useState<AttendanceFilters>(() => {
    const now = new Date();
    const today = format(now, 'yyyy-MM-dd');
    const firstDay = format(startOfMonth(now), 'yyyy-MM-dd');
    return {
      page: 1,
      limit: 50,
      sort_by: 'created_at',
      sort_order: 'DESC',
      start_date: firstDay,
      end_date: today,
    };
  });

  // Função para carregar opções dos dropdowns
  const loadDropdownOptions = async () => {
    try {
      const response = await api.get<{
        success: boolean;
        data: {
          attendants: string[];
          departments: string[];
          connections: string[];
        };
      }>('/attendances/options');
      
      if (response.success) {
        setAttendantOptions(response.data.attendants);
        setDepartmentOptions(response.data.departments);
        setConnectionOptions(response.data.connections);
      }
    } catch (error) {
      console.error('Erro ao carregar opções dos dropdowns:', error);
    }
  };

  // Atualizar período e recarregar dados
  const handlePeriodChange = (newPeriod: PeriodType) => {
    setPeriodType(newPeriod);
    
    // Calcular datas do período
    const dates = getPeriodDates(newPeriod, customDateFrom, customDateTo);
    
    // Criar novos filtros com as datas do período
    const newFilters: AttendanceFilters = {
      ...filters,
      start_date: dates.from,
      end_date: dates.to,
      page: 1,
    };
    
    // Atualizar estado dos filtros
    setFilters(newFilters);
    setPage(1);
    
    // Recarregar dados imediatamente com os novos filtros
    loadAttendances(newFilters, 1);
    loadStatistics(newFilters);
  };

  // Carregar atendimentos
  const loadAttendances = async (customFilters?: AttendanceFilters, customPage?: number) => {
    setLoading(true);
    try {
      const filtersToUse = customFilters || filters;
      const pageToUse = customPage !== undefined ? customPage : page;
      const params = new URLSearchParams();
      Object.entries({ ...filtersToUse, page: pageToUse, limit }).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });

      const response = await api.get<{
        success: boolean;
        data: Attendance[];
        pagination: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
      }>(`/attendances?${params.toString()}`);
      
      if (response.success) {
        setAttendances(response.data);
        setTotal(response.pagination.total);
        setTotalPages(response.pagination.totalPages);
      }
    } catch (error: any) {
      console.error('Erro ao carregar atendimentos:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao carregar atendimentos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Carregar estatísticas
  const loadStatistics = async (customFilters?: AttendanceFilters) => {
    try {
      interface StatisticsResponse {
        success: boolean;
        data: AttendanceStatistics;
      }
      // Usar filtros customizados se fornecidos, senão usar os filtros atuais
      const filtersToUse = customFilters || filters;
      
      // Aplicar filtros de data nas estatísticas também
      const params = new URLSearchParams();
      if (filtersToUse.start_date) params.append('start_date', filtersToUse.start_date);
      if (filtersToUse.end_date) params.append('end_date', filtersToUse.end_date);
      
      const url = params.toString() 
        ? `/attendances/statistics?${params.toString()}`
        : '/attendances/statistics';
      
      const response: StatisticsResponse = await api.get(url);
      console.log('📊 [Attendances] Resposta completa:', JSON.stringify(response, null, 2));
      if (response && response.success && response.data) {
        console.log('📊 [Attendances] Dados de estatísticas:', {
          total: response.data.total,
          porTipo: response.data.porTipo,
          porSLA: response.data.porSLA,
          porDepartamento: response.data.porDepartamento ? Object.keys(response.data.porDepartamento).length : 0,
          porDia: response.data.porDia ? Object.keys(response.data.porDia).length : 0,
          porHora: response.data.porHora ? Object.keys(response.data.porHora).length : 0,
          porAtendente: response.data.porAtendente ? Object.keys(response.data.porAtendente).length : 0,
          porDepartamentoKeys: response.data.porDepartamento ? Object.keys(response.data.porDepartamento) : [],
          porDiaKeys: response.data.porDia ? Object.keys(response.data.porDia) : [],
          porHoraKeys: response.data.porHora ? Object.keys(response.data.porHora) : [],
          porAtendenteKeys: response.data.porAtendente ? Object.keys(response.data.porAtendente) : [],
        });
        setStatistics(response.data);
      } else {
        console.warn('⚠️ [Attendances] Resposta de estatísticas sem sucesso ou sem dados:', response);
        // Mesmo sem dados, definir estatísticas vazias para mostrar a seção
        setStatistics({
          total: 0,
          porTipo: { ativo: 0, receptivo: 0 },
          porSLA: { dentroPrazo: 0, foraPrazo: 0 },
          porDepartamento: {},
          porAtendente: {},
          porDia: {},
          porHora: {},
        });
      }
    } catch (error) {
      console.error('❌ [Attendances] Erro ao carregar estatísticas:', error);
      // Mesmo em caso de erro, definir estatísticas vazias para mostrar a seção
      setStatistics({
        total: 0,
        porTipo: { ativo: 0, receptivo: 0 },
        porSLA: { dentroPrazo: 0, foraPrazo: 0 },
        porDepartamento: {},
        porDia: {},
        porHora: {},
      });
      toast({
        title: 'Aviso',
        description: 'Não foi possível carregar as estatísticas completas. Alguns gráficos podem estar vazios.',
        variant: 'destructive',
      });
    }
  };

  // Contar filtros ativos
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.protocol) count++;
    if (filters.customer_name) count++;
    if (filters.customer_number) count++;
    if (selectedAttendants.length > 0) count++;
    if (selectedDepartments.length > 0) count++;
    if (selectedConnections.length > 0) count++;
    if (filters.type) count++;
    if (filters.sla_status) count++;
    if (filters.start_date) count++;
    if (filters.end_date) count++;
    return count;
  };

  // Carregar arquivos exportados

  // Exportar para Excel (assíncrono)
  const handleExportExcel = async () => {
    setExporting(true);
    setExportType('excel');
    try {
      toast({
        title: 'Solicitando exportação...',
        description: 'Sua exportação foi adicionada à fila. Você será notificado quando estiver pronta.',
      });

      const response = await api.requestExport('excel', filters);
      
      if (response.success) {
        toast({
          title: 'Exportação solicitada',
          description: 'A exportação está sendo processada em segundo plano. Os arquivos aparecerão abaixo quando prontos.',
        });
        
        toast({
          title: 'Exportação solicitada',
          description: 'A exportação está sendo processada. Abra a Central de Relatórios para acompanhar o progresso.',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao solicitar exportação',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
      setExportType(null);
    }
  };


  // Exportar para PDF
  const handleExportPDF = async () => {
    setExporting(true);
    setExportType('pdf');
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });

      toast({
        title: 'Exportando...',
        description: 'Gerando arquivo PDF. Isso pode levar alguns instantes.',
      });

      const blob = await api.downloadFile(`/attendances/export/pdf?${params.toString()}`);

      // Criar link de download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = `atendimentos_${new Date().toISOString().split('T')[0]}_${total}_registros.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Sucesso',
        description: `${total} atendimento(s) exportado(s) para PDF com sucesso!`,
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao exportar atendimentos',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
      setExportType(null);
    }
  };

  // Aplicar filtros
  const handleApplyFilters = () => {
    // Atualizar filtros com valores dos multiselect
    const newFilters: AttendanceFilters = {
      ...filters,
      atendente_abertura: selectedAttendants.length > 0 ? selectedAttendants.join(',') : undefined,
      departamento_abertura: selectedDepartments.length > 0 ? selectedDepartments.join(',') : undefined,
      connection: selectedConnections.length > 0 ? selectedConnections.join(',') : undefined,
      page: 1
    };
    setFilters(newFilters);
    setPage(1);
    // Carregar com os novos filtros imediatamente
    loadAttendances(newFilters, 1);
    loadStatistics(newFilters);
  };

  // Limpar filtros
  const handleClearFilters = () => {
    // Restaurar filtros de data para o período atual
    const dates = getPeriodDates(periodType, customDateFrom, customDateTo);
    const clearedFilters: AttendanceFilters = {
      page: 1,
      limit: 50,
      sort_by: 'created_at',
      sort_order: 'DESC',
      start_date: dates.from,
      end_date: dates.to,
    };
    setFilters(clearedFilters);
    setSelectedAttendants([]);
    setSelectedDepartments([]);
    setSelectedConnections([]);
    setPage(1);
    // Carregar com filtros limpos imediatamente
    loadAttendances(clearedFilters, 1);
    loadStatistics(clearedFilters);
  };

  // Função para ordenação
  const handleSort = (column: string) => {
    const newOrder = filters.sort_by === column && filters.sort_order === 'ASC' ? 'DESC' : 'ASC';
    setFilters(prev => ({
      ...prev,
      sort_by: column,
      sort_order: newOrder,
      page: 1
    }));
    setPage(1);
    loadAttendances();
  };

  // Formatar intervalo de tempo
  const formatInterval = (interval: any): string => {
    if (!interval) return '-';
    const parts = [];
    if (interval.days) parts.push(`${interval.days}d`);
    if (interval.hours) parts.push(`${interval.hours}h`);
    if (interval.minutes) parts.push(`${interval.minutes}m`);
    if (interval.seconds) parts.push(`${interval.seconds}s`);
    return parts.length > 0 ? parts.join(' ') : '-';
  };

  // Formatar data
  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR');
  };

  // Badge de status SLA
  const getSLABadge = (status?: string) => {
    if (!status) return null;
    
    if (status.toLowerCase().includes('dentro')) {
      return <Badge className="bg-green-500 text-white">Dentro do Prazo</Badge>;
    } else {
      return <Badge className="bg-red-500 text-white">Fora do Prazo</Badge>;
    }
  };

  // Badge de tipo
  const getTypeBadge = (type?: string) => {
    if (!type) return null;
    
    if (type === 'Ativo') {
      return <Badge className="bg-blue-500 text-white">Ativo</Badge>;
    } else {
      return <Badge className="bg-purple-500 text-white">Receptivo</Badge>;
    }
  };

  // Inicializar datas personalizadas quando período custom for selecionado
  useEffect(() => {
    if (periodType === 'custom' && (!customDateFrom || !customDateTo)) {
      const now = new Date();
      const firstDay = format(startOfMonth(now), 'yyyy-MM-dd');
      const today = format(now, 'yyyy-MM-dd');
      setCustomDateFrom(firstDay);
      setCustomDateTo(today);
      // Atualizar filtros com as datas iniciais
      setFilters(prev => ({
        ...prev,
        start_date: firstDay,
        end_date: today,
      }));
    }
  }, [periodType]);

  useEffect(() => {
    loadAttendances();
    loadStatistics();
    loadDropdownOptions();
  }, [page]);


  // Recarregar quando filtros de data mudarem
  useEffect(() => {
    // Evitar recarregar no mount inicial (já é feito no useEffect acima)
    if (filters.start_date || filters.end_date) {
      const timer = setTimeout(() => {
        loadAttendances();
        loadStatistics(filters);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [filters.start_date, filters.end_date]);

  // Debug: log quando statistics mudar
  useEffect(() => {
    console.log('🔄 [Attendances] Statistics atualizado:', {
      hasStatistics: !!statistics,
      total: statistics?.total,
      hasPorDepartamento: !!(statistics?.porDepartamento && Object.keys(statistics.porDepartamento).length > 0),
      hasPorDia: !!(statistics?.porDia && Object.keys(statistics.porDia).length > 0),
      hasPorHora: !!(statistics?.porHora && Object.keys(statistics.porHora).length > 0),
    });
  }, [statistics]);

  // Debug: verificar se statistics está definido
  console.log('🎨 [Attendances] Renderizando componente. Statistics:', statistics ? 'Definido' : 'Não definido');

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-montserrat font-bold text-gray-900 flex items-center gap-2">
          <Activity className="w-7 h-7 text-primary-600" />
          Gestão de Atendimentos
        </h1>
        <p className="text-gray-600 mt-1">Acompanhe e analise todos os atendimentos</p>
        {user?.role === 'cliente' && user?.client_id && (
          <div className="mt-2 flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-md inline-block">
            <AlertCircle className="w-4 h-4" />
            <span>Exibindo apenas atendimentos da sua empresa</span>
          </div>
        )}
      </div>

      {/* SEÇÃO 1: Gráficos Visuais - SEMPRE VISÍVEL */}
      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-montserrat font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-primary-600" />
              Análise Visual dos Dados
            </CardTitle>
            
            {/* Seletor de Período */}
            <div className="flex items-center gap-3">
              <Select
                value={periodType}
                onValueChange={(value: PeriodType) => handlePeriodChange(value)}
              >
                <SelectTrigger className="w-[180px]">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current_month">
                    Mês Atual
                  </SelectItem>
                  <SelectItem value="last_month">
                    Mês Anterior
                  </SelectItem>
                  <SelectItem value="custom">
                    Personalizado
                  </SelectItem>
                </SelectContent>
              </Select>
              
              {periodType === 'custom' && (
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => {
                      const newFrom = e.target.value;
                      setCustomDateFrom(newFrom);
                      if (newFrom && customDateTo) {
                      const updatedFilters = {
                        ...filters,
                        start_date: newFrom,
                        end_date: customDateTo,
                        page: 1,
                      };
                        setFilters(updatedFilters);
                        setPage(1);
                        setTimeout(() => {
                          loadAttendances(updatedFilters, 1);
                          loadStatistics(updatedFilters);
                        }, 0);
                      }
                    }}
                    className="w-[140px]"
                    placeholder="De"
                  />
                  <span className="text-gray-500">até</span>
                  <Input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => {
                      const newTo = e.target.value;
                      setCustomDateTo(newTo);
                      if (customDateFrom && newTo) {
                        const updatedFilters = {
                          ...filters,
                          start_date: customDateFrom,
                          end_date: newTo,
                          page: 1,
                        };
                        setFilters(updatedFilters);
                        setPage(1);
                        setTimeout(() => {
                          loadAttendances(updatedFilters, 1);
                          loadStatistics(updatedFilters);
                        }, 0);
                      }
                    }}
                    className="w-[140px]"
                    placeholder="Até"
                  />
                </div>
              )}
              
              {periodType !== 'custom' && (
                <div className="text-sm text-gray-600">
                  {(() => {
                    const dates = getPeriodDates(periodType);
                    return `${format(new Date(dates.from + 'T00:00:00'), 'dd/MM/yyyy')} - ${format(new Date(dates.to + 'T00:00:00'), 'dd/MM/yyyy')}`;
                  })()}
                </div>
              )}
              
              {periodType === 'custom' && customDateFrom && customDateTo && (
                <div className="text-sm text-gray-600">
                  {format(new Date(customDateFrom + 'T00:00:00'), 'dd/MM/yyyy')} - {format(new Date(customDateTo + 'T00:00:00'), 'dd/MM/yyyy')}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
        
        {!statistics ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando dados para os gráficos...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Gráfico: Consumo por Fila/Departamento */}
              {statistics.porDepartamento && Object.keys(statistics.porDepartamento).length > 0 ? (
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg font-montserrat text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary-600" />
                  Consumo por Fila
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={{
                    enviadas: {
                      label: 'Receptivos',
                      color: '#8b5cf6',
                    },
                    recebidas: {
                      label: 'Ativos',
                      color: '#a78bfa',
                    },
                  }}
                  className="h-[400px] w-full"
                >
                  <BarChart
                    data={Object.entries(statistics.porDepartamento)
                      .sort((a, b) => b[1].total - a[1].total)
                      .slice(0, 10)
                      .map(([fila, data]) => ({
                        fila: fila.length > 20 ? fila.substring(0, 20) + '...' : fila,
                        enviadas: data.receptivo,
                        recebidas: data.ativo,
                        total: data.total,
                      }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis 
                      dataKey="fila" 
                      type="category" 
                      width={140}
                      tick={{ fontSize: 10 }}
                    />
                    <ChartTooltip 
                      content={<ChartTooltipContent />}
                    />
                    <Bar dataKey="enviadas" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="recebidas" fill="#a78bfa" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
            ) : (
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-lg font-montserrat text-gray-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary-600" />
                    Consumo por Fila
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center h-[400px] text-gray-500">
                    <p>Nenhum dado de departamento disponível</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Gráfico: Contatos por Dia do Mês */}
            {statistics.porDia && Object.keys(statistics.porDia).length > 0 ? (
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg font-montserrat text-gray-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary-600" />
                  Número de Contatos por Dia do Mês
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={{
                    ativos: {
                      label: 'Receptivos',
                      color: '#8b5cf6',
                    },
                    receptivos: {
                      label: 'Ativos',
                      color: '#a78bfa',
                    },
                  }}
                  className="h-[400px] w-full"
                >
                  <BarChart
                    data={Object.entries(statistics.porDia)
                      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                      .map(([dia, data]) => ({
                        dia: parseInt(dia),
                        ativos: data.receptivo,
                        receptivos: data.ativo,
                        total: data.total,
                      }))}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <XAxis 
                      dataKey="dia" 
                      tick={{ fontSize: 11 }}
                      interval={Math.max(0, Math.floor(Object.keys(statistics.porDia).length / 15))}
                      label={{ value: 'Dia do Mês', position: 'insideBottom', offset: -5 }}
                    />
                    <YAxis 
                      tick={{ fontSize: 11 }} 
                      label={{ value: 'Número de Contatos', angle: -90, position: 'insideLeft' }}
                    />
                    <ChartTooltip 
                      content={<ChartTooltipContent />}
                    />
                    <Bar dataKey="ativos" stackId="a" fill="#8b5cf6" />
                    <Bar dataKey="receptivos" stackId="a" fill="#a78bfa" />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
            ) : (
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-lg font-montserrat text-gray-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary-600" />
                    Número de Contatos por Dia do Mês
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center h-[400px] text-gray-500">
                    <p>Nenhum dado por dia disponível</p>
                  </div>
                </CardContent>
              </Card>
              )}
            </div>

            {/* Gráfico: Contatos por Hora do Dia - Full Width */}
            {statistics.porHora && Object.keys(statistics.porHora).length > 0 ? (
              <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg font-montserrat text-gray-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary-600" />
                  Número de Contatos por Hora do Dia
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={{
                    ativos: {
                      label: 'Receptivos',
                      color: '#8b5cf6',
                    },
                    receptivos: {
                      label: 'Ativos',
                      color: '#a78bfa',
                    },
                  }}
                  className="h-[400px] w-full"
                >
                  <BarChart
                    data={Object.entries(statistics.porHora)
                      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                      .map(([hora, data]) => ({
                        hora: `${parseInt(hora).toString().padStart(2, '0')}:00`,
                        horaNum: parseInt(hora),
                        ativos: data.receptivo,
                        receptivos: data.ativo,
                        total: data.total,
                      }))}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <XAxis 
                      dataKey="hora" 
                      tick={{ fontSize: 10 }}
                      interval={1}
                      label={{ value: 'Hora do Dia', position: 'insideBottom', offset: -5 }}
                    />
                    <YAxis 
                      tick={{ fontSize: 11 }} 
                      label={{ value: 'Número de Contatos', angle: -90, position: 'insideLeft' }}
                    />
                    <ChartTooltip 
                      content={<ChartTooltipContent />}
                    />
                    <Bar dataKey="ativos" stackId="a" fill="#8b5cf6" />
                    <Bar dataKey="receptivos" stackId="a" fill="#a78bfa" />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
            ) : (
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-lg font-montserrat text-gray-900 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary-600" />
                    Número de Contatos por Hora do Dia
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center h-[400px] text-gray-500">
                    <p>Nenhum dado por hora disponível</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
        </CardContent>
      </Card>

      {/* SEÇÃO 1.5: Insights por Atendente */}
      {statistics && statistics.porAtendente && Object.keys(statistics.porAtendente).length > 0 && (
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-xl font-montserrat font-bold text-gray-900 flex items-center gap-2">
              <User className="w-6 h-6 text-primary-600" />
              Insights por Atendente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Gráfico: Top 10 Atendentes por Volume */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-lg font-montserrat text-gray-900 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary-600" />
                    Top 10 Atendentes por Volume
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ChartContainer
                    config={{
                      total: {
                        label: 'Total de Atendimentos',
                        color: '#8b5cf6',
                      },
                    }}
                    className="h-[400px] w-full"
                  >
                    <BarChart
                      data={Object.entries(statistics.porAtendente)
                        .sort((a, b) => b[1].total - a[1].total)
                        .slice(0, 10)
                        .map(([atendente, data]) => ({
                          atendente: atendente.length > 20 ? atendente.substring(0, 20) + '...' : atendente,
                          total: data.total,
                        }))}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis 
                        dataKey="atendente" 
                        type="category" 
                        width={140}
                        tick={{ fontSize: 10 }}
                      />
                      <ChartTooltip 
                        content={<ChartTooltipContent />}
                      />
                      <Bar dataKey="total" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ChartContainer>
                </CardContent>
              </Card>

              {/* Gráfico: Distribuição Ativo vs Receptivo por Atendente */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-lg font-montserrat text-gray-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary-600" />
                    Distribuição Ativo vs Receptivo (Top 10)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ChartContainer
                    config={{
                      receptivos: {
                        label: 'Receptivos',
                        color: '#8b5cf6',
                      },
                      ativos: {
                        label: 'Ativos',
                        color: '#a78bfa',
                      },
                    }}
                    className="h-[400px] w-full"
                  >
                    <BarChart
                      data={Object.entries(statistics.porAtendente)
                        .sort((a, b) => b[1].total - a[1].total)
                        .slice(0, 10)
                        .map(([atendente, data]) => ({
                          atendente: atendente.length > 15 ? atendente.substring(0, 15) + '...' : atendente,
                          receptivos: data.receptivo,
                          ativos: data.ativo,
                          total: data.total,
                        }))}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis 
                        dataKey="atendente" 
                        type="category" 
                        width={140}
                        tick={{ fontSize: 10 }}
                      />
                      <ChartTooltip 
                        content={<ChartTooltipContent />}
                      />
                      <Bar dataKey="receptivos" stackId="a" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="ativos" stackId="a" fill="#a78bfa" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ChartContainer>
                </CardContent>
              </Card>
            </div>

            {/* Gráfico: Performance SLA por Atendente */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg font-montserrat text-gray-900 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary-600" />
                  Performance SLA por Atendente (Top 15)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={{
                    dentroPrazo: {
                      label: 'Dentro do Prazo',
                      color: '#10b981',
                    },
                    foraPrazo: {
                      label: 'Fora do Prazo',
                      color: '#ef4444',
                    },
                  }}
                  className="h-[500px] w-full"
                >
                  <BarChart
                    data={Object.entries(statistics.porAtendente)
                      .sort((a, b) => b[1].total - a[1].total)
                      .slice(0, 15)
                      .map(([atendente, data]) => ({
                        atendente: atendente.length > 20 ? atendente.substring(0, 20) + '...' : atendente,
                        dentroPrazo: data.dentroPrazo,
                        foraPrazo: data.foraPrazo,
                        total: data.total,
                        taxaSLA: data.total > 0 ? ((data.dentroPrazo / data.total) * 100).toFixed(1) : '0',
                      }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis 
                      dataKey="atendente" 
                      type="category" 
                      width={180}
                      tick={{ fontSize: 10 }}
                    />
                    <ChartTooltip 
                      content={<ChartTooltipContent />}
                    />
                    <Bar dataKey="dentroPrazo" stackId="a" fill="#10b981" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="foraPrazo" stackId="a" fill="#ef4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      )}

      {/* SEÇÃO 2: Filtros */}
      <div className="space-y-6">
        <div className="flex items-center justify-between border-b border-gray-200 pb-3">
          <h2 className="text-xl font-montserrat font-bold text-gray-900 flex items-center gap-2">
            <Filter className="w-6 h-6 text-primary-600" />
            Filtros de Busca
          </h2>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            className="border-gray-300 relative"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            {getActiveFiltersCount() > 0 && (
              <span className="absolute -top-2 -right-2 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {getActiveFiltersCount()}
              </span>
            )}
          </Button>
        </div>

        {showFilters && (
        <Card className="glass-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-montserrat text-gray-900 flex items-center gap-2">
                <Filter className="w-5 h-5 text-primary-600" />
                Filtros Avançados
              </CardTitle>
              {getActiveFiltersCount() > 0 && (
                <Badge className="bg-primary-600 text-white">
                  {getActiveFiltersCount()} filtro(s) ativo(s)
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-700">Protocolo</Label>
                <Input
                  placeholder="Buscar por protocolo"
                  value={filters.protocol || ''}
                  onChange={(e) => setFilters({ ...filters, protocol: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-gray-700">Nome do Cliente</Label>
                <Input
                  placeholder="Buscar por nome"
                  value={filters.customer_name || ''}
                  onChange={(e) => setFilters({ ...filters, customer_name: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-gray-700">Telefone</Label>
                <Input
                  placeholder="Buscar por telefone"
                  value={filters.customer_number || ''}
                  onChange={(e) => setFilters({ ...filters, customer_number: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-gray-700">Atendente</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        "w-full justify-between mt-1",
                        !selectedAttendants.length && "text-muted-foreground"
                      )}
                    >
                      {selectedAttendants.length > 0 
                        ? `${selectedAttendants.length} selecionado(s)` 
                        : "Selecionar atendente(s)"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput placeholder="Buscar atendente..." />
                      <CommandList>
                        <CommandEmpty>Nenhum atendente encontrado.</CommandEmpty>
                        <CommandGroup>
                          {attendantOptions.map((attendant) => (
                            <CommandItem
                              key={attendant}
                              value={attendant}
                              onSelect={(currentValue) => {
                                setSelectedAttendants(prev => 
                                  prev.includes(currentValue)
                                    ? prev.filter(item => item !== currentValue)
                                    : [...prev, currentValue]
                                );
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedAttendants.includes(attendant) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {attendant}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="text-gray-700">Departamento</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        "w-full justify-between mt-1",
                        !selectedDepartments.length && "text-muted-foreground"
                      )}
                    >
                      {selectedDepartments.length > 0 
                        ? `${selectedDepartments.length} selecionado(s)` 
                        : "Selecionar departamento(s)"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput placeholder="Buscar departamento..." />
                      <CommandList>
                        <CommandEmpty>Nenhum departamento encontrado.</CommandEmpty>
                        <CommandGroup>
                          {departmentOptions.map((department) => (
                            <CommandItem
                              key={department}
                              value={department}
                              onSelect={(currentValue) => {
                                setSelectedDepartments(prev => 
                                  prev.includes(currentValue)
                                    ? prev.filter(item => item !== currentValue)
                                    : [...prev, currentValue]
                                );
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedDepartments.includes(department) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {department}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="text-gray-700">Conexão</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        "w-full justify-between mt-1",
                        !selectedConnections.length && "text-muted-foreground"
                      )}
                    >
                      {selectedConnections.length > 0 
                        ? `${selectedConnections.length} selecionado(s)` 
                        : "Selecionar conexão(ões)"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput placeholder="Buscar conexão..." />
                      <CommandList>
                        <CommandEmpty>Nenhuma conexão encontrada.</CommandEmpty>
                        <CommandGroup>
                          {connectionOptions.map((connection) => (
                            <CommandItem
                              key={connection}
                              value={connection}
                              onSelect={(currentValue) => {
                                setSelectedConnections(prev => 
                                  prev.includes(currentValue)
                                    ? prev.filter(item => item !== currentValue)
                                    : [...prev, currentValue]
                                );
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedConnections.includes(connection) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {connection}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="text-gray-700">Tipo</Label>
                <Select
                  value={filters.type || ''}
                  onValueChange={(value) => setFilters({ ...filters, type: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ativo">Ativo</SelectItem>
                    <SelectItem value="Receptivo">Receptivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-gray-700">Status SLA</Label>
                <Select
                  value={filters.sla_status || ''}
                  onValueChange={(value) => setFilters({ ...filters, sla_status: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dentro do Prazo">Dentro do Prazo</SelectItem>
                    <SelectItem value="Fora do Prazo">Fora do Prazo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-gray-700">Data Início</Label>
                <Input
                  type="date"
                  value={filters.start_date || ''}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-gray-700">Data Fim</Label>
                <Input
                  type="date"
                  value={filters.end_date || ''}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6 items-center justify-between">
              <div className="flex gap-3">
                <Button
                  onClick={handleApplyFilters}
                  className="bg-primary-600 hover:bg-primary-700 text-white border-0"
                  disabled={loading}
                  style={{ backgroundColor: '#2563eb' }}
                >
                  <Search className="w-4 h-4 mr-2" />
                  {loading ? 'Aplicando...' : 'Aplicar Filtros'}
                </Button>
                
                <Button
                  onClick={handleClearFilters}
                  variant="outline"
                  className="border-gray-300 bg-white hover:bg-gray-50"
                  disabled={loading || getActiveFiltersCount() === 0}
                >
                  <X className="w-4 h-4 mr-2" />
                  Limpar Filtros
                </Button>
              </div>
              {getActiveFiltersCount() > 0 && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{total}</span> registro(s) encontrado(s) com os filtros aplicados
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* SEÇÃO 3: Dados Estatísticos e Tabela */}
      <div className="space-y-6">
        <div className="flex items-center justify-between border-b border-gray-200 pb-3">
          <h2 className="text-xl font-montserrat font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-primary-600" />
            Dados e Estatísticas
          </h2>
          <div className="flex gap-3 items-center">
            <Button
              onClick={handleExportExcel}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              {exporting && exportType === 'excel' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Exportando...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Excel
                </>
              )}
            </Button>

            <Button
              onClick={handleExportPDF}
              disabled={exporting}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {exporting && exportType === 'pdf' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Exportando...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  PDF
                </>
              )}
            </Button>

            {/* Botão Central de Relatórios */}
            <Button
              variant="outline"
              onClick={() => setShowReportsCenter(true)}
              className="ml-4 gap-2"
            >
              <FolderOpen className="w-4 h-4" />
              Central de Relatórios
            </Button>
          </div>
        </div>

        {/* Cards de Estatísticas */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="glass-card border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total de Atendimentos</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{statistics.total}</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <BarChart3 className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-l-4 border-l-green-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Atendimentos Ativos</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{statistics.porTipo.ativo}</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg">
                    <TrendingUp className="w-8 h-8 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-l-4 border-l-purple-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Atendimentos Receptivos</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{statistics.porTipo.receptivo}</p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <TrendingDown className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-l-4 border-l-emerald-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Dentro do SLA</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{statistics.porSLA.dentroPrazo}</p>
                    <p className="text-xs text-emerald-600 font-medium mt-1">
                      {statistics.total > 0 
                        ? `${((statistics.porSLA.dentroPrazo / statistics.total) * 100).toFixed(1)}%`
                        : '0%'
                      }
                    </p>
                  </div>
                  <div className="bg-emerald-100 p-3 rounded-lg">
                    <AlertCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabela */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-lg font-montserrat text-gray-900 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-primary-600" />
                Atendimentos ({total} registros)
              </span>
              <span className="text-sm font-normal text-gray-600">
                Página {page} de {totalPages}
              </span>
            </CardTitle>
          </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : attendances.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>Nenhum atendimento encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold text-gray-700">Protocolo</TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('customer_name')}
                    >
                      <div className="flex items-center gap-2">
                        Cliente
                        {filters.sort_by === 'customer_name' ? (
                          filters.sort_order === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : <ArrowUpDown className="h-4 w-4 opacity-50" />}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('customer_number')}
                    >
                      <div className="flex items-center gap-2">
                        Telefone
                        {filters.sort_by === 'customer_number' ? (
                          filters.sort_order === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : <ArrowUpDown className="h-4 w-4 opacity-50" />}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('type')}
                    >
                      <div className="flex items-center gap-2">
                        Tipo
                        {filters.sort_by === 'type' ? (
                          filters.sort_order === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : <ArrowUpDown className="h-4 w-4 opacity-50" />}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('atendente_abertura')}
                    >
                      <div className="flex items-center gap-2">
                        Atendente
                        {filters.sort_by === 'atendente_abertura' ? (
                          filters.sort_order === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : <ArrowUpDown className="h-4 w-4 opacity-50" />}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('departamento_abertura')}
                    >
                      <div className="flex items-center gap-2">
                        Departamento
                        {filters.sort_by === 'departamento_abertura' ? (
                          filters.sort_order === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : <ArrowUpDown className="h-4 w-4 opacity-50" />}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-2">
                        Data Início
                        {filters.sort_by === 'created_at' ? (
                          filters.sort_order === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : <ArrowUpDown className="h-4 w-4 opacity-50" />}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('total_time_seconds')}
                    >
                      <div className="flex items-center gap-2">
                        Tempo Total
                        {filters.sort_by === 'total_time_seconds' ? (
                          filters.sort_order === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : <ArrowUpDown className="h-4 w-4 opacity-50" />}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('sla_status')}
                    >
                      <div className="flex items-center gap-2">
                        SLA
                        {filters.sort_by === 'sla_status' ? (
                          filters.sort_order === 'ASC' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : <ArrowUpDown className="h-4 w-4 opacity-50" />}
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attendances.map((attendance) => (
                    <TableRow 
                      key={attendance.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedAttendance(attendance)}
                    >
                      <TableCell className="font-mono text-sm text-gray-900">
                        {attendance.protocol}
                      </TableCell>
                      <TableCell className="text-gray-700">
                        {attendance.customer_name}
                      </TableCell>
                      <TableCell className="text-gray-600 font-mono text-sm">
                        {attendance.customer_number}
                      </TableCell>
                      <TableCell>
                        {getTypeBadge(attendance.type)}
                      </TableCell>
                      <TableCell className="text-gray-700">
                        {renderAttendant(attendance.atendente_abertura)}
                      </TableCell>
                      <TableCell className="text-gray-700">
                        {attendance.departamento_abertura || '-'}
                      </TableCell>
                      <TableCell className="text-gray-600 text-sm">
                        {formatDate(attendance.start_date)}
                      </TableCell>
                      <TableCell className="text-gray-700">
                        {formatInterval(attendance.total_time)}
                      </TableCell>
                      <TableCell>
                        {getSLABadge(attendance.sla_status)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAttendance(attendance);
                          }}
                        >
                          Ver Detalhes
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <Button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                variant="outline"
                className="border-gray-300"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>
              
              <span className="text-gray-600 text-sm">
                Página {page} de {totalPages}
              </span>
              
              <Button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                variant="outline"
                className="border-gray-300"
              >
                Próxima
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Modal de Detalhes */}
      <Dialog open={!!selectedAttendance} onOpenChange={() => setSelectedAttendance(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-montserrat text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-primary-600" />
              Detalhes do Atendimento
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Protocolo: {selectedAttendance?.protocol}
            </DialogDescription>
          </DialogHeader>

          {selectedAttendance && (
            <div className="space-y-6 mt-4">
              {/* Informações do Cliente */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <User className="w-5 h-5 text-primary-600" />
                  Informações do Cliente
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Nome</p>
                    <p className="text-gray-900 font-medium">{selectedAttendance.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Telefone</p>
                    <p className="text-gray-900 font-mono">{selectedAttendance.customer_number}</p>
                  </div>
                </div>
              </div>

              {/* Informações do Atendimento */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary-600" />
                  Informações do Atendimento
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Tipo</p>
                    <div className="mt-1">{getTypeBadge(selectedAttendance.type)}</div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status SLA</p>
                    <div className="mt-1">{getSLABadge(selectedAttendance.sla_status)}</div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Atendente</p>
                    <p className="text-gray-900">
                      {renderAttendant(selectedAttendance.atendente_abertura)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Departamento</p>
                    <p className="text-gray-900">{selectedAttendance.departamento_abertura || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Conexão</p>
                    <p className="text-gray-900">{selectedAttendance.connection || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">SLA (Dias)</p>
                    <p className="text-gray-900">{selectedAttendance.sla_days || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Datas e Tempos */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary-600" />
                  Datas e Tempos
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Data Início</p>
                    <p className="text-gray-900">{formatDate(selectedAttendance.start_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Data Fim</p>
                    <p className="text-gray-900">{formatDate(selectedAttendance.end_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Tempo Total</p>
                    <p className="text-gray-900 font-mono">{formatInterval(selectedAttendance.total_time)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Primeira Espera</p>
                    <p className="text-gray-900 font-mono">{formatInterval(selectedAttendance.first_wait_time)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Espera Média</p>
                    <p className="text-gray-900 font-mono">{formatInterval(selectedAttendance.average_wait_time)}</p>
                  </div>
                </div>
              </div>

              {/* Atendentes e Departamentos */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Abertura e Fechamento</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Atendente Abertura</p>
                    <p className="text-gray-900">
                      {renderAttendant(selectedAttendance.atendente_abertura)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Atendente Fechamento</p>
                    <p className="text-gray-900">
                      {renderAttendant(selectedAttendance.atendente_fechamento)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Departamento Abertura</p>
                    <p className="text-gray-900">{selectedAttendance.departamento_abertura || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Departamento Fechamento</p>
                    <p className="text-gray-900">{selectedAttendance.departamento_fechamento || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Resumo e Assuntos */}
              {(selectedAttendance.summary || selectedAttendance.subjects) && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Detalhes</h3>
                  {selectedAttendance.summary && (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Resumo</p>
                      <p className="text-gray-900 bg-white p-3 rounded border border-gray-200">{selectedAttendance.summary}</p>
                    </div>
                  )}
                  {selectedAttendance.subjects && (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Assuntos</p>
                      <p className="text-gray-900 bg-white p-3 rounded border border-gray-200">{selectedAttendance.subjects}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {selectedAttendance.tags && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Tags</h3>
                  <p className="text-gray-900">{selectedAttendance.tags}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Central de Relatórios */}
      <ReportsCenter
        open={showReportsCenter}
        onOpenChange={setShowReportsCenter}
      />
    </div>
  );
};

const Attendances: React.FC = () => {
  return (
    <RoleBasedNavigation>
      <Layout>
        <AttendancesContent />
      </Layout>
    </RoleBasedNavigation>
  );
};

export default Attendances;
